import type { ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Icon } from "@core/assets";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  EmptyState,
  ErrorPanel,
  PageHeader,
  SkeletonRows,
  Tabs,
} from "@core/components";
import { hasAction } from "@core/permissions";
import { isArchived } from "../../_shared";
import { useCohort } from "../../detail";

/** The six phases of a cohort, in order. Only the first (the appel) is open for now. */
const PHASES = [
  "appel",
  "evaluation",
  "roster",
  "setup",
  "programme",
  "closure",
] as const;

function KeyNumber({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col gap-xxs px-lg py-xs">
      <span className="display-sm-semibold text-(--text-primary)">{value}</span>
      <span className="text-sm-regular text-(--text-secondary)">{label}</span>
    </div>
  );
}

/**
 * One cohort's page (Figma "Cohort created"): the name with its code and status, the six phase
 * tabs (only « Appel » is open, the others read « Verrouillée »), the four key numbers and the
 * list of appels, empty for a new cohort.
 *
 * The cohort is loaded by its route before this opens (a missing or out-of-scope cohort is the
 * not-found state, shown by the route). Everything on the page comes from the server's answer:
 * the counts, and for an appel list only what this account is allowed to see.
 */
export function CohortScreen({
  cohortId,
  appels: appelsSection,
}: {
  cohortId: string;
  /** The « Appels » section, composed by the route from the appels module. */
  appels?: ReactNode;
}) {
  const { t } = useTranslation("cohorts");
  const navigate = useNavigate();
  const cohort = useCohort(cohortId);

  if (cohort.isPending) return <SkeletonRows rows={3} />;
  if (cohort.isError) {
    return (
      <ErrorPanel
        isRetrying={cohort.isFetching}
        onRetry={() => {
          void cohort.refetch();
        }}
      />
    );
  }

  const data = cohort.data;

  return (
    <>
      <PageHeader
        title={data.name}
        onBack={() => {
          void navigate({ to: "/cohorts" });
        }}
        actions={
          hasAction(data.available_actions, "edit") ||
          hasAction(data.available_actions, "restore") ? (
            <Button
              variant="secondary"
              onPress={() => {
                void navigate({
                  to: "/cohorts/$cohortId/settings",
                  params: { cohortId: data.id },
                });
              }}
            >
              <Icon name="settings" size={16} />
              {t("settings.open")}
            </Button>
          ) : null
        }
        chips={
          <>
            <Badge outlined>{data.code}</Badge>
            <Badge>
              {isArchived(data) ? t("status.archived") : t("status.preparing")}
            </Badge>
          </>
        }
      />

      <Tabs
        variant="phase"
        aria-label={t("phases.label")}
        value="appel"
        onChange={() => undefined}
        items={PHASES.map((phase) => ({
          id: phase,
          label: t(`phases.${phase}`),
          caption:
            phase === "appel"
              ? isArchived(data)
                ? t("status.archived")
                : t("status.preparing")
              : t("phases.locked"),
          isDisabled: phase !== "appel",
        }))}
      />

      <section
        aria-label={t("numbers.label")}
        className="grid grid-cols-2 gap-y-md divide-(--border-default) lg:grid-cols-4 lg:divide-x"
      >
        <KeyNumber
          value={data.dashboard_counts.submitted}
          label={t("numbers.submitted")}
        />
        <KeyNumber
          value={data.dashboard_counts.receivable}
          label={t("numbers.receivable")}
        />
        <KeyNumber
          value={data.dashboard_counts.to_process}
          label={t("numbers.to-process")}
        />
        <KeyNumber
          value={data.dashboard_counts.places}
          label={t("numbers.places")}
        />
      </section>

      {appelsSection ?? (
        <section className="flex flex-col gap-sm">
          <h2 className="text-md-semibold text-(--text-primary)">
            {t("appels.title")}
          </h2>
          <EmptyState
            illustration="empty"
            title={t("appels.empty")}
            className="min-h-75 justify-center"
          />
        </section>
      )}
    </>
  );
}
